"use client";

import Image from "next/image";
import { useEffect, useRef, useState } from "react";

import { CcButton } from "@/components/ui/CcButton";
import { CcSanjayaEyebrow } from "@/components/ui/CcSanjayaEyebrow";
import { devSectionAttr } from "@/lib/dev/section-attr";
import { useCcPrefersReducedMotion } from "@/lib/use-cc-prefers-reduced-motion";

import type { SiSubpageCopy } from "./si-page-copy";

type CcSiHowProps = {
  copy: SiSubpageCopy["how"];
};

type Step = SiSubpageCopy["how"]["steps"][number];

/**
 * One step's clip. It's only fetched and played while its step is the
 * active one and the section is near the screen, so scrolling past four
 * steps never downloads four videos at once; the optimised still frame
 * shows until then. Phones get the lighter 960×540 clip.
 */
function StepClip({ step, active, reduced }: { step: Step; active: boolean; reduced: boolean }) {
  const ref = useRef<HTMLVideoElement>(null);
  const [near, setNear] = useState(false);

  useEffect(() => {
    const video = ref.current;
    if (!video || near) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setNear(true);
      },
      { rootMargin: "300px 0px" },
    );
    observer.observe(video);
    return () => observer.disconnect();
  }, [near]);

  useEffect(() => {
    const video = ref.current;
    if (!video || reduced || !near) return;
    if (active) {
      if (!video.getAttribute("src")) {
        const small = window.matchMedia("(max-width: 810px)").matches;
        video.src = small && step.video.srcSmall ? step.video.srcSmall : step.video.src;
      }
      void video.play().catch(() => {});
    } else {
      video.pause();
    }
  }, [active, near, reduced, step.video.src, step.video.srcSmall]);

  return (
    <>
      <Image
        className="cc-si-how__clip"
        src={step.video.poster}
        alt=""
        fill
        sizes="(min-width: 1200px) 620px, 92vw"
        quality={75}
      />
      <video ref={ref} className="cc-si-how__clip" muted loop playsInline preload="none" tabIndex={-1} />
    </>
  );
}

/**
 * "How a system integration works" after cartage.ai's sections: the steps
 * scroll past while a pinned video panel swaps clip and event card. On
 * phones each step shows its own clip inline.
 */
export function CcSiHow({ copy }: CcSiHowProps) {
  const reduced = useCcPrefersReducedMotion();
  const [active, setActive] = useState(0);
  const stepRefs = useRef<(HTMLLIElement | null)[]>([]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) setActive(Number((entry.target as HTMLElement).dataset.index));
        }
      },
      { rootMargin: "-45% 0px -45% 0px" },
    );
    for (const step of stepRefs.current) if (step) observer.observe(step);
    return () => observer.disconnect();
  }, []);

  return (
    <section
      className="cc-section cc-si-how"
      id="integration-how"
      aria-labelledby="cc-si-how-title"
      data-cc-nav="light"
      {...devSectionAttr("integration-how")}
    >
      <div className="cc-si-how__container">
        <header className="cc-si-how__header">
          <CcSanjayaEyebrow theme="on-subtle">{copy.eyebrow}</CcSanjayaEyebrow>
          <h2 id="cc-si-how-title" className="cc-si-how__title">
            {copy.title}
          </h2>
          <p className="cc-si-how__intro">{copy.intro}</p>
        </header>

        <div className="cc-si-how__layout">
          <div className="cc-si-how__visual" aria-hidden="true">
            <div className="cc-si-how__sticky">
              <div className="cc-si-how__screen">
                {copy.steps.map((step, index) => (
                  <div key={step.id} className="cc-si-how__panel" data-active={index === active ? "true" : undefined}>
                    <StepClip step={step} active={index === active} reduced={reduced} />
                    <div className="cc-si-event">
                      <span className="cc-si-event__label">{step.card.label}</span>
                      <strong>{step.card.title}</strong>
                      {step.card.lines.map((line) => (
                        <code key={line}>{line}</code>
                      ))}
                    </div>
                  </div>
                ))}
                <span className="cc-si-how__counter">
                  {String(active + 1).padStart(2, "0")} / {String(copy.steps.length).padStart(2, "0")}
                </span>
              </div>
            </div>
          </div>

          <ol className="cc-si-how__steps">
            {copy.steps.map((step, index) => (
              <li
                key={step.id}
                ref={(node) => {
                  stepRefs.current[index] = node;
                }}
                data-index={index}
                data-active={index === active ? "true" : undefined}
                className="cc-si-how__step"
              >
                <span className="cc-si-how__num">{String(index + 1).padStart(2, "0")}</span>
                <h3>{step.title}</h3>
                <p>{step.text}</p>
                <div className="cc-si-how__inline" aria-hidden="true">
                  <Image
                    src={step.video.poster}
                    alt=""
                    width={1600}
                    height={900}
                    sizes="92vw"
                    quality={75}
                    loading="lazy"
                  />
                  <div className="cc-si-event">
                    <span className="cc-si-event__label">{step.card.label}</span>
                    <strong>{step.card.title}</strong>
                    {step.card.lines.map((line) => (
                      <code key={line}>{line}</code>
                    ))}
                  </div>
                </div>
              </li>
            ))}
            <li className="cc-si-how__cta">
              <CcButton bookCallPopup bookCallVariant="integration" label={copy.cta} />
              <small>{copy.ctaNote}</small>
            </li>
          </ol>
        </div>
      </div>
    </section>
  );
}
